import * as React from 'react';
import { Button, StackLayout } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

/**
 * Props for the MoveItemsFooter component
 * @interface MoveItemsFooterProps
 * @property {boolean} isMoveButtonDisabled - Controls whether the move button is disabled
 * @property {() => void} onClose - Callback function when the cancel button is clicked
 * @property {() => void} onMove - Callback function when the move button is clicked
 */
export interface MoveItemsFooterProps {
  isMoveButtonDisabled: boolean;
  onClose: () => void;
  onMove: () => void;
}

/**
 * Footer component for the MoveItemsModal that contains action buttons.
 * Displays Cancel and Move buttons with appropriate states and callbacks.
 *
 * @component
 * @example
 * ```tsx
 * <MoveItemsFooter
 *   isMoveButtonDisabled={false}
 *   onClose={() => {}}
 *   onMove={() => {}}
 * />
 * ```
 */
export const MoveItemsFooter: React.FC<MoveItemsFooterProps> = ({
  onClose,
  onMove,
  isMoveButtonDisabled,
}) => {
  const { formatMessage } = useIntl();

  return (
    <StackLayout
      orientation="horizontal"
      spacing="$one"
      horizontalAlignment="end"
    >
      <Button onClick={onClose} priority="tertiary" quiet>
        {formatMessage({ id: 'CANCEL' })}
      </Button>
      <Button onClick={onMove} disabled={isMoveButtonDisabled}>
        {formatMessage({ id: 'MOVE_ITEM' })}
      </Button>
    </StackLayout>
  );
};
